import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { FINANCIAL_PRODUCT } from '@brighte/validation';
import { Header } from '@components/Header';
import { Loader } from '@components/Loader/Loader';
import { datadogRum } from '@datadog/browser-rum';
import { ApplicationFormStatus, QuoteStatus as Status } from '@graphql/index';
import { usePrepop } from '@hooks';
import { Box } from '@spark-web/box';
import { Stack } from '@spark-web/stack';
import { accessibleQuote, getTrackingId } from '@utils';
import { ApplicationStatus } from '../';
import { useApplicationData, useStepperActions } from '../../provider';
import LandTitleEligibilityCheck from '../LandTitle/LandTitleEligibilityCheck';
import QuoteStatusPage from '../QuoteStatusPage/QuoteStatusPage';
import SubmittedApplication from '../SubmittedApplication/SubmittedApplication';

const FINANCE_STEP_INDEX = 1;

function FormPage() {
  const { isLoading, data: quote } = usePrepop();
  const [landTitleCompleted, setLandTitleCompleted] = useState(false);
  const { onNext } = useStepperActions();

  const submitApplication = useApplicationData('SubmitApplication');
  const isApplicationSubmitted = submitApplication?.status === ApplicationFormStatus.Submitted;
  const trackingId = getTrackingId();
  const { user, error: authError } = useAuth0();

  useEffect(() => {
    // setUser to dataDog for session details
    if (user) {
      datadogRum.setUser({
        trackingId,
        id: user?.id,
        applicationType: quote?.financialProduct.name,
      });
      datadogRum.startSessionReplayRecording();
    }
  }, [user, authError]);

  const flags = useFlags();
  const isEligibilityCheckEnabled = flags['enableEligiblityCheckForm'] ?? false;

  const isBhselEligibilityCheckAccessible = quote?.financialProduct?.id === FINANCIAL_PRODUCT.BHESL;

  const renderFormPage = () => {
    switch (true) {
      case isLoading:
        return <Loader />;
      case isApplicationSubmitted:
        return <ApplicationStatus />;
      case isBhselEligibilityCheckAccessible && isEligibilityCheckEnabled && !landTitleCompleted:
        return (
          <LandTitleEligibilityCheck
            onComplete={() => {
              setLandTitleCompleted(true);
              onNext(FINANCE_STEP_INDEX);
            }}
          />
        );
      case quote?.status === Status.COMPLETED:
        return <SubmittedApplication />;
      default:
        return <QuoteStatusPage status={quote?.status} />;
    }
  };

  return (
    <>
      <Header financialProductId={quote?.financialProduct?.id ?? ''} />
      <Box paddingY="medium">
        <Box paddingY="medium">
          <Stack gap="xxlarge">{renderFormPage()}</Stack>
        </Box>
      </Box>
    </>
  );
}

export default FormPage;
